import type {
  ActivityAdapter,
  ActivityCompletionMetrics,
  ActivityExecutionContext
} from "@/app/integration/contracts";
import { getEnglishRhythmFeedbackCue } from "@/audio/curriculum-catalog/english-feedback-cues";
import type { RhythmPhrasePayload } from "@/content/english";
import {
  advanceHintEscalation,
  createHintEscalationState,
  createRetryFeedback,
  createSuccessFeedback,
  type ActiveHintStage,
  type ActivityFeedback,
  type HintEscalationState
} from "@/game/feedback";
import {
  getAdjacentId,
  type InputMethod,
  type NavigationDirection
} from "@/game/input";
import {
  createRhythmState,
  reduceRhythmState,
  rhythmPayloadSchema,
  type RhythmCuePresentation,
  type RhythmResult,
  type RhythmState,
  type RhythmTapResult
} from "@/game/templates/rhythm";

import { requireEnglishActivityAudioKey } from "./audio-bridge";

export type EnglishRhythmFocusDirection =
  | NavigationDirection
  | "first"
  | "last";

export type EnglishRhythmPlayerAction =
  | {
      readonly type: "start-model";
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "model-ended";
      readonly playbackRequest: number;
    }
  | {
      readonly type: "media-unavailable";
      readonly playbackRequest: number;
    }
  | {
      readonly type: "select-token";
      readonly token: string;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "move-focus";
      readonly currentTokenIndex: number;
      readonly direction: EnglishRhythmFocusDirection;
    }
  | { readonly type: "activate-focused" }
  | { readonly type: "request-hint" }
  | {
      readonly type: "feedback-audio-finished";
      readonly playbackRequest: number;
    }
  | {
      readonly type: "replay-model";
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "reset" };

export interface EnglishRhythmPlayerState {
  readonly rhythm: RhythmState;
  readonly retryCount: number;
  readonly hintState: HintEscalationState;
  readonly hintFeedbackStage: ActiveHintStage | null;
  readonly retryFeedbackAudioComplete: boolean;
  readonly feedback: ActivityFeedback | null;
  readonly focusedTokenIndex: number | null;
  readonly playbackInputMethod: InputMethod | null;
  readonly playbackRequest: number;
}

export type EnglishRhythmCueStatus =
  | "pending"
  | "current"
  | "on-beat"
  | "off-beat"
  | "wrong-order";

export interface EnglishRhythmCueRenderModel {
  readonly index: number;
  readonly token: string;
  readonly atMs: number;
  readonly presentation: RhythmCuePresentation;
  readonly status: EnglishRhythmCueStatus;
}

export interface EnglishRhythmTokenRenderModel {
  readonly audioPath: string;
  readonly id: string;
  readonly index: number;
  readonly token: string;
  readonly disabled: boolean;
  readonly focused: boolean;
}

export interface EnglishRhythmPlayerRenderModel {
  readonly phase: RhythmState["phase"];
  readonly audio: {
    readonly audioKey: ReturnType<typeof requireEnglishActivityAudioKey>;
    readonly inputMethod: InputMethod | null;
    readonly shouldPlay: boolean;
    readonly waitingForFeedback: boolean;
    readonly playbackRequest: number;
  };
  readonly caption: {
    readonly text: string;
    readonly synchronized: boolean;
    readonly mediaFallback: boolean;
  };
  readonly cues: readonly EnglishRhythmCueRenderModel[];
  readonly tokens: readonly EnglishRhythmTokenRenderModel[];
  readonly feedback: {
    readonly audioPath: string;
    readonly autoPlay: boolean;
    readonly kind: "hint" | "retry";
    readonly playbackRequest: number;
    readonly stage: ActiveHintStage;
    readonly message: string;
  } | null;
  readonly reducedMotion: boolean;
  readonly canStart: boolean;
  readonly canReplayModel: boolean;
  readonly result: RhythmResult | null;
}

function createInitialState(
  payload: RhythmPhrasePayload,
  context: ActivityExecutionContext
): EnglishRhythmPlayerState {
  return {
    rhythm: createRhythmState(payload, {
      reducedMotion: context.reducedMotion
    }),
    retryCount: 0,
    hintState: createHintEscalationState(),
    hintFeedbackStage: null,
    retryFeedbackAudioComplete: true,
    feedback: null,
    focusedTokenIndex: null,
    playbackInputMethod: null,
    playbackRequest: 0
  };
}

function startModel(
  state: EnglishRhythmPlayerState,
  payload: RhythmPhrasePayload,
  context: ActivityExecutionContext,
  inputMethod: InputMethod,
  replay: boolean
): EnglishRhythmPlayerState {
  const action = replay ? { type: "replay" as const } : { type: "start" as const };
  const rhythm = reduceRhythmState(state.rhythm, payload, action, {
    now: context.nowMs
  });

  if (rhythm === state.rhythm) {
    return state;
  }

  return {
    ...state,
    rhythm,
    feedback: null,
    hintFeedbackStage: null,
    retryFeedbackAudioComplete: true,
    focusedTokenIndex: null,
    playbackInputMethod: inputMethod,
    playbackRequest: state.playbackRequest + 1
  };
}

function createRetryState(
  state: EnglishRhythmPlayerState,
  payload: RhythmPhrasePayload,
  context: ActivityExecutionContext,
  inputMethod: InputMethod
): EnglishRhythmPlayerState {
  const transition = createRetryFeedback(state.hintState);
  const ready = createRhythmState(payload, {
    reducedMotion: state.rhythm.reducedMotion
  });
  const started = reduceRhythmState(ready, payload, { type: "start" }, {
    now: context.nowMs
  });
  const replayCount = state.rhythm.replayCount + 1;
  const withReplayCount = { ...started, replayCount };
  const rhythm = state.rhythm.mediaUnavailable
    ? reduceRhythmState(
        withReplayCount,
        payload,
        { type: "media-unavailable" },
        { now: context.nowMs }
      )
    : withReplayCount;

  return {
    rhythm,
    retryCount: state.retryCount + 1,
    hintState: transition.hintState,
    hintFeedbackStage: null,
    retryFeedbackAudioComplete: false,
    feedback: transition.feedback,
    focusedTokenIndex: null,
    playbackInputMethod: inputMethod,
    playbackRequest:
      state.playbackRequest + (state.rhythm.mediaUnavailable ? 0 : 1)
  };
}

function selectToken(
  state: EnglishRhythmPlayerState,
  payload: RhythmPhrasePayload,
  token: string,
  inputMethod: InputMethod,
  context: ActivityExecutionContext
): EnglishRhythmPlayerState {
  const rhythm = reduceRhythmState(
    state.rhythm,
    payload,
    { type: "tap", token },
    { now: context.nowMs }
  );

  if (rhythm === state.rhythm) {
    return state;
  }

  if (rhythm.result === null) {
    return {
      ...state,
      rhythm,
      feedback: null,
      hintFeedbackStage: null,
      retryFeedbackAudioComplete: true
    };
  }

  if (rhythm.result.correctRate < 1) {
    return createRetryState(
      { ...state, rhythm },
      payload,
      context,
      inputMethod
    );
  }

  return {
    ...state,
    rhythm,
    feedback: createSuccessFeedback(),
    hintFeedbackStage: null,
    retryFeedbackAudioComplete: true,
    focusedTokenIndex: null
  };
}

function getFocusedTokenIndex(
  state: EnglishRhythmPlayerState,
  payload: RhythmPhrasePayload,
  direction: EnglishRhythmFocusDirection
): number | null {
  const ids = payload.answerTokens.map(
    (_token, index) => "rhythm-token-" + (index + 1)
  );

  if (direction === "first") {
    return ids.length === 0 ? null : 0;
  }

  if (direction === "last") {
    return ids.length === 0 ? null : ids.length - 1;
  }

  const currentId =
    state.focusedTokenIndex === null
      ? null
      : ids[state.focusedTokenIndex] ?? null;
  const nextId = getAdjacentId(ids, currentId, direction);

  return nextId === null ? null : ids.indexOf(nextId);
}

function synchronizeTokenFocus(
  state: EnglishRhythmPlayerState,
  payload: RhythmPhrasePayload,
  currentTokenIndex: number
): EnglishRhythmPlayerState {
  const isKnownToken = payload.answerTokens[currentTokenIndex] !== undefined;

  if (!isKnownToken || state.focusedTokenIndex === currentTokenIndex) {
    return state;
  }

  return { ...state, focusedTokenIndex: currentTokenIndex };
}

function reduceRhythmPlayer(
  state: EnglishRhythmPlayerState,
  payload: RhythmPhrasePayload,
  action: EnglishRhythmPlayerAction,
  context: ActivityExecutionContext
): EnglishRhythmPlayerState {
  switch (action.type) {
    case "reset":
      return {
        ...createInitialState(payload, context),
        playbackRequest: state.playbackRequest
      };
    case "start-model":
      return startModel(state, payload, context, action.inputMethod, false);
    case "replay-model":
      return state.rhythm.phase === "ready"
        ? startModel(state, payload, context, action.inputMethod, false)
        : startModel(state, payload, context, action.inputMethod, true);
    case "model-ended": {
      if (
        action.playbackRequest !== state.playbackRequest ||
        (state.feedback?.kind === "retry" &&
          !state.retryFeedbackAudioComplete)
      ) {
        return state;
      }

      const rhythm = reduceRhythmState(
        state.rhythm,
        payload,
        { type: "model-ended" },
        { now: context.nowMs }
      );
      return rhythm === state.rhythm ? state : { ...state, rhythm };
    }
    case "media-unavailable": {
      if (action.playbackRequest !== state.playbackRequest) {
        return state;
      }

      const rhythm = reduceRhythmState(
        state.rhythm,
        payload,
        { type: "media-unavailable" },
        { now: context.nowMs }
      );
      return rhythm === state.rhythm ? state : { ...state, rhythm };
    }
    case "request-hint": {
      const hintState = advanceHintEscalation(state.hintState);

      return {
        ...state,
        feedback: null,
        hintFeedbackStage: hintState.stage,
        hintState,
        retryFeedbackAudioComplete: true
      };
    }
    case "feedback-audio-finished":
      return state.feedback?.kind === "retry" &&
        !state.retryFeedbackAudioComplete &&
        action.playbackRequest === state.hintState.hintCount + state.retryCount
        ? { ...state, retryFeedbackAudioComplete: true }
        : state;
    case "move-focus": {
      if (state.rhythm.phase !== "awaiting-input") {
        return state;
      }

      const synchronizedState = synchronizeTokenFocus(
        state,
        payload,
        action.currentTokenIndex
      );
      const focusedTokenIndex = getFocusedTokenIndex(
        synchronizedState,
        payload,
        action.direction
      );

      return focusedTokenIndex === null
        ? synchronizedState
        : { ...synchronizedState, focusedTokenIndex };
    }
    case "activate-focused": {
      const token =
        state.focusedTokenIndex === null
          ? undefined
          : payload.answerTokens[state.focusedTokenIndex];
      return token === undefined
        ? state
        : selectToken(state, payload, token, "keyboard", context);
    }
    case "select-token":
      return selectToken(
        state,
        payload,
        action.token,
        action.inputMethod,
        context
      );
  }
}

function getMetrics(
  state: EnglishRhythmPlayerState
): ActivityCompletionMetrics {
  const result = state.rhythm.result;

  if (result === null || result.correctRate < 1) {
    return {
      completed: false,
      score: 0,
      correctRate: 0,
      hintCount: state.hintState.hintCount,
      retryCount: state.retryCount
    };
  }

  const correctRate = 1 / (state.retryCount + 1);

  return {
    completed: true,
    score: Math.round(result.score * correctRate),
    correctRate,
    hintCount: state.hintState.hintCount,
    retryCount: state.retryCount
  };
}

function getCueStatus(
  tap: RhythmTapResult | undefined,
  current: boolean
): EnglishRhythmCueStatus {
  if (tap === undefined) {
    return current ? "current" : "pending";
  }
  if (!tap.tokenCorrect) {
    return "wrong-order";
  }
  return tap.onBeat ? "on-beat" : "off-beat";
}

function requireRhythmTokenAudioPath(
  payload: RhythmPhrasePayload,
  token: string
): string {
  const audioPath = payload.tokenAudio[token];

  if (audioPath === undefined) {
    throw new Error("Missing rhythm token audio for " + token);
  }

  return audioPath;
}

function getFeedbackModel(
  state: EnglishRhythmPlayerState,
  modelAudioIsPlaying: boolean
): EnglishRhythmPlayerRenderModel["feedback"] {
  const retryFeedback =
    state.feedback?.kind === "retry" ? state.feedback : null;
  const stage = retryFeedback?.hintStage ?? state.hintFeedbackStage;

  if (stage === null) {
    return null;
  }

  const cue = getEnglishRhythmFeedbackCue(stage);

  return {
    audioPath: cue.audioPath,
    autoPlay:
      retryFeedback === null ||
      (!state.retryFeedbackAudioComplete && !modelAudioIsPlaying),
    kind: retryFeedback === null ? "hint" : "retry",
    message: cue.text,
    playbackRequest: state.hintState.hintCount + state.retryCount,
    stage
  };
}

function toRenderModel(
  state: EnglishRhythmPlayerState,
  payload: RhythmPhrasePayload
): EnglishRhythmPlayerRenderModel {
  const mediaIsPlaying =
    state.rhythm.phase === "model-playing" &&
    !state.rhythm.mediaUnavailable;
  const waitingForFeedback =
    state.feedback?.kind === "retry" &&
    !state.retryFeedbackAudioComplete &&
    mediaIsPlaying;

  return {
    phase: state.rhythm.phase,
    audio: {
      audioKey: requireEnglishActivityAudioKey(payload.modelAudio),
      inputMethod: state.playbackInputMethod,
      shouldPlay: mediaIsPlaying,
      waitingForFeedback,
      playbackRequest: state.playbackRequest
    },
    caption: {
      text: state.rhythm.caption,
      synchronized: mediaIsPlaying,
      mediaFallback: state.rhythm.mediaUnavailable
    },
    cues: payload.answerTokens.map((token, index) => ({
      index,
      token,
      atMs: payload.beatCuesMs[index] ?? 0,
      presentation: state.rhythm.cuePresentation,
      status: getCueStatus(
        state.rhythm.taps[index],
        state.rhythm.phase === "awaiting-input" &&
          state.rhythm.taps.length === index
      )
    })),
    tokens: payload.answerTokens.map((token, index) => ({
      audioPath: requireRhythmTokenAudioPath(payload, token),
      id: "rhythm-token-" + (index + 1),
      index,
      token,
      disabled: state.rhythm.phase !== "awaiting-input",
      focused: state.focusedTokenIndex === index
    })),
    feedback: getFeedbackModel(state, mediaIsPlaying),
    reducedMotion: state.rhythm.reducedMotion,
    canStart: state.rhythm.phase === "ready",
    canReplayModel:
      state.rhythm.phase === "awaiting-input" ||
      state.rhythm.phase === "model-playing",
    result: state.rhythm.result
  };
}

function parseRhythmPhrasePayload(value: unknown): RhythmPhrasePayload {
  const parsed = rhythmPayloadSchema.parse(value);
  const [firstToken, ...remainingTokens] = parsed.answerTokens;

  if (firstToken === undefined) {
    throw new Error("Rhythm phrase requires at least one answer token");
  }

  return {
    ...parsed,
    answerTokens: [firstToken, ...remainingTokens]
  };
}

export const englishRhythmPhrasesAdapter: ActivityAdapter<
  RhythmPhrasePayload,
  EnglishRhythmPlayerState,
  EnglishRhythmPlayerAction,
  EnglishRhythmPlayerRenderModel
> = {
  activityType: "rhythm-phrases",
  parsePayload: parseRhythmPhrasePayload,
  createInitialState,
  reduce: reduceRhythmPlayer,
  getMetrics: (state) => getMetrics(state),
  toRenderModel
};

export const englishRhythmPlayerActions = {
  requestHint: (): EnglishRhythmPlayerAction => ({ type: "request-hint" }),
  replay: (): EnglishRhythmPlayerAction => ({ type: "reset" })
};
